import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  ChatBubbleOutline,
  KeyboardDoubleArrowRight,
  KeyboardDoubleArrowLeft,
  FormatListBulleted,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import CommentsSection from "../CommentsSection";
import { getLessonMetadata } from "../../utils/lessonMetadata";

// Helper to extract h2 and h3 headings from HTML content
const extractHeadings = (html) => {
  if (!html) return [];
  const headings = [];
  const regex = /<(h2|h3)([^>]*)>([\s\S]*?)<\/\1>/gi;
  let match;
  let index = 0;
  while ((match = regex.exec(html)) !== null) {
    const tag = match[1].toLowerCase();
    const rawContent = match[3];
    
    // Extract text recursively (strip any nested HTML tags)
    const cleanText = rawContent.replace(/<[^>]*>/g, "").trim();
    
    // Generate matching slug ID
    const id = cleanText.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
    
    headings.push({
      tag,
      text: cleanText,
      id: id || `heading-${index}`,
      level: tag === "h2" ? 2 : 3
    });
    index++;
  }
  return headings;
};

export default function LessonUtilityPanel({
  selectedContent,
  rightOpen,
  onToggleRight,
  panelRef, // Reference to the middle scroll container
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [tabValue, setTabValue] = useState(0);
  const [activeHeadingId, setActiveHeadingId] = useState("");
  const [scrollProgress, setScrollProgress] = useState(0);

  const metadata = getLessonMetadata(selectedContent?.subject, selectedContent?.content_id, selectedContent?.content_text);
  const totalMinutes = parseInt(metadata?.duration) || 10;
  const remainingMinutes = Math.max(1, Math.ceil(totalMinutes * (1 - scrollProgress / 100)));

  const headings = extractHeadings(selectedContent?.content_text);

  // Track scroll position to highlight active heading
  useEffect(() => {
    const scrollContainer = panelRef?.current;
    if (!scrollContainer || headings.length === 0) return;

    const handleScroll = () => {
      const scrollContainerTop = scrollContainer.getBoundingClientRect().top;
      
      let currentActive = "";
      // Find the heading closest to the top of the scroll container
      for (const heading of headings) {
        const el = document.getElementById(heading.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          // If the element has scrolled past the top (or is close to it)
          if (rect.top - scrollContainerTop <= 100) {
            currentActive = heading.id;
          }
        }
      }
      setActiveHeadingId(currentActive || headings[0].id);

      // Calculate scroll progress percentage
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight - scrollContainer.clientHeight;
      const progress = scrollHeight > 0 ? Math.min(100, Math.max(0, (scrollTop / scrollHeight) * 100)) : 0;
      setScrollProgress(progress);
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    handleScroll(); // Run once initially
    
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [headings, panelRef]);

  const handleHeadingClick = (id) => {
    const el = document.getElementById(id);
    if (el && panelRef?.current) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const scrollbarStyles = {
    scrollbarWidth: "thin",
    scrollbarColor: isDark
      ? "rgba(255, 255, 255, 0.2) transparent"
      : "rgba(0, 0, 0, 0.2) transparent",
    "&::-webkit-scrollbar": { width: "6px" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: isDark ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.35)",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "transparent",
        color: theme.palette.text.primary,
      }}
    >
      {/* Sticky Header with Actions & Tabs */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "transparent",
          borderBottom: `1px solid ${theme.palette.divider}`,
          p: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        {rightOpen ? (
          <Tabs
            value={tabValue}
            onChange={(e, val) => setTabValue(val)}
            variant="fullWidth"
            size="small"
            sx={{
              minHeight: 36,
              flex: 1,
              "& .MuiTabs-indicator": {
                background: "linear-gradient(135deg, #e50914 0%, #6a0dad 100%)",
                height: 3,
                borderRadius: "3px 3px 0 0",
              },
              "& .MuiTab-root": {
                minHeight: 36,
                py: 0.5,
                fontSize: "0.78rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: theme.palette.text.secondary,
                transition: "color 0.2s",
                "&.Mui-selected": {
                  color: isDark ? "#ffa4a2" : "#e50914",
                },
              },
            }}
          >
            <Tab
              label="On This Page"
              icon={<FormatListBulleted sx={{ fontSize: 16 }} />}
              iconPosition="start"
            />
            <Tab
              label="Discussion"
              icon={<ChatBubbleOutline sx={{ fontSize: 15 }} />}
              iconPosition="start"
            />
          </Tabs>
        ) : (
          <Box sx={{ width: 0, overflow: "hidden" }} />
        )}

        <Tooltip title={rightOpen ? "Collapse Sidebar" : "Expand Sidebar"} placement="top">
          <IconButton
            size="small"
            onClick={onToggleRight}
            sx={{
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.primary.main },
              transition: "transform 0.3s ease",
              transform: rightOpen ? "rotate(0deg)" : "rotate(180deg)",
            }}
          >
            {rightOpen ? (
              <KeyboardDoubleArrowRight sx={{ fontSize: 22 }} />
            ) : (
              <KeyboardDoubleArrowLeft sx={{ fontSize: 22, ml: -1 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Tab Panels Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          p: rightOpen ? 2 : 0,
          ...scrollbarStyles,
          opacity: rightOpen ? 1 : 0,
          transition: "opacity 0.2s",
        }}
      >
        {rightOpen && selectedContent && (
          <>
            {tabValue === 0 && (
              <Box>
                {headings.length > 0 ? (
                  <Box>
                    {/* Lesson Progress & Est. Remaining Time */}
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            textTransform: "uppercase",
                            color: theme.palette.text.secondary,
                            letterSpacing: 0.5,
                          }}
                        >
                          On This Page
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: isDark ? "#ffa4a2" : "#e50914",
                            fontSize: "0.72rem",
                          }}
                        >
                          {Math.round(scrollProgress)}% read • ~{remainingMinutes} min left
                        </Typography>
                      </Box>
                      <Box sx={{ width: "100%", height: 4, bgcolor: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", borderRadius: 10, overflow: "hidden" }}>
                        <Box
                          sx={{
                            height: "100%",
                            width: `${scrollProgress}%`,
                            background: "linear-gradient(90deg, #e50914 0%, #6a0dad 100%)",
                            borderRadius: 10,
                            transition: "width 0.1s ease-out",
                          }}
                        />
                      </Box>
                    </Box>

                    <Box
                      component="ul"
                      sx={{
                        listStyle: "none",
                        p: 0,
                        m: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 0.5,
                        borderLeft: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`,
                        pl: 0,
                        ml: 0.5,
                      }}
                    >
                      {headings.map((heading) => {
                        const isActive = activeHeadingId === heading.id;
                        return (
                          <Box
                            component="li"
                            key={heading.id}
                            onClick={() => handleHeadingClick(heading.id)}
                            sx={{
                              fontSize: heading.level === 2 ? "0.82rem" : "0.76rem",
                              fontWeight: isActive ? 700 : 500,
                              color: isActive
                                ? (isDark ? "#ffa4a2" : "#e50914")
                                : theme.palette.text.secondary,
                              cursor: "pointer",
                              pl: heading.level === 2 ? 1.5 : 3,
                              py: 0.4,
                              borderLeft: isActive
                                ? `3px solid ${isDark ? "#ffa4a2" : "#e50914"}`
                                : "3px solid transparent",
                              marginLeft: "-2px",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                color: isDark ? "#ffcdd2" : "#e50914",
                              },
                            }}
                          >
                            {heading.text}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}>
                    <Typography variant="body2">📂 No headings on this page</Typography>
                  </Box>
                )}
              </Box>
            )}

            {tabValue === 1 && (
              <CommentsSection
                postId={selectedContent.id}
                contentId={selectedContent.content_id}
              />
            )}
          </>
        )}

        {rightOpen && !selectedContent && (
          <Box sx={{ textAlign: "center", mt: 4, color: "text.secondary" }}>
            <Typography variant="body2">
              Select a topic to view outline and discussion.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
